// src/projects/project_finances/ProfitLossTab.js
import React from 'react';

export default function ProfitLossTab({
  totalInvoiced = 0,
  totalPaid = 0,
  totalExpenses = 0,
  grossProfit = 0,   // accrual: revenue − direct job costs
  cashProfit = 0,    // cash basis: collected − spent
  profitMargin = '0%'
}) {
  // Always show exact cents (round to the cent first to avoid float drift).
  const fmtMoney = (n) => (Math.round((Number(n) || 0) * 100) / 100)
    .toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <div className="space-y-10 max-md:space-y-5">
      <div>
        <h2 className="text-3xl max-md:text-xl font-bold mb-2 max-md:mb-1">Profit & Loss</h2>
        <p className="text-zinc-400">Project-level financial performance (Cash Basis)</p>
      </div>

      {/* Key P&L Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-md:gap-2.5">
        <div className="bg-gray-900 rounded-2xl max-md:rounded-xl p-8 max-md:p-4 border border-gray-800/50 shadow-xl">
          <p className="text-zinc-400 text-sm max-md:text-[11px] font-medium uppercase tracking-wider">Total Collected</p>
          <p className="text-5xl max-md:text-xl font-bold text-indigo-400 mt-4 max-md:mt-1">
            ${fmtMoney(totalPaid)}
          </p>
          <p className="text-xs text-zinc-500 mt-2">Total Invoiced: ${fmtMoney(totalInvoiced)}</p>
        </div>

        <div className="bg-gray-900 rounded-2xl max-md:rounded-xl p-8 max-md:p-4 border border-gray-800/50 shadow-xl">
          <p className="text-zinc-400 text-sm max-md:text-[11px] font-medium uppercase tracking-wider">Total Expenses</p>
          <p className="text-5xl max-md:text-xl font-bold text-red-400 mt-4 max-md:mt-1">
            ${fmtMoney(totalExpenses)}
          </p>
        </div>

        <div className="bg-gray-900 rounded-2xl max-md:rounded-xl p-8 max-md:p-4 border border-gray-800/50 shadow-xl">
          <p className="text-zinc-400 text-sm max-md:text-[11px] font-medium uppercase tracking-wider">Net Cash Profit</p>
          <p className={`text-5xl max-md:text-xl font-bold mt-4 max-md:mt-1 ${cashProfit >= 0 ? 'text-emerald-400' : 'text-red-500'}`}>
            ${fmtMoney(cashProfit)}
          </p>
          <p className="text-sm text-emerald-500 mt-2 font-medium">Gross profit ${fmtMoney(grossProfit)} · {profitMargin} margin</p>
        </div>
      </div>

      {/* Breakdown Table */}
      <div className="bg-gray-900 rounded-2xl overflow-hidden border border-gray-800/50 shadow-2xl">
        <div className="p-6 border-b border-gray-800 bg-gray-950/30">
          <h3 className="text-xl font-semibold">Financial Breakdown</h3>
        </div>
        
        <div className="p-8 max-md:p-4 space-y-4 max-md:space-y-1">
          <div className="flex justify-between items-center py-3">
            <span className="text-zinc-400">Total Payments Received (Manual + Invoices)</span>
            <span className="font-semibold text-white">${fmtMoney(totalPaid)}</span>
          </div>

          <div className="flex justify-between items-center py-3">
            <span className="text-zinc-400">Operating Expenses (Materials, Labor, etc.)</span>
            <span className="font-semibold text-red-400">-${fmtMoney(totalExpenses)}</span>
          </div>

          <div className="h-px bg-gray-800 my-4"></div>

          <div className="flex justify-between items-center py-2 text-xl">
            <span className="font-bold text-white">Project Net (Cash)</span>
            <span className={`font-black ${cashProfit >= 0 ? 'text-emerald-400' : 'text-red-500'}`}>
              ${fmtMoney(cashProfit)}
            </span>
          </div>
        </div>

        <div className="bg-gray-950/50 p-6 border-t border-gray-800">
          <div className="flex items-start gap-3 text-zinc-500 text-sm italic">
            <div className="mt-1 w-2 h-2 rounded-full bg-indigo-500 shadow-[0_0_8px_rgba(99,102,241,0.8)]"></div>
            <p>
              This view reflects actual cash flow. Outstanding invoices are not counted 
              toward profit until payment is recorded.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}